import { type LicenseDTO, LicenseStatusEnum } from '@shared/dto/config';

// Customer names that should not see remediation reports
const CUSTOMERS_WITHOUT_REMEDIATION_REPORTS: string[] = [];

/**
 * Check if remediation reports should be shown for the given customer
 */
export function shouldShowRemediationReports(customerName: string | null | undefined): boolean {
  if (!customerName) {
    return true;
  }
  const normalizedCustomerName = customerName.toLowerCase();
  return !CUSTOMERS_WITHOUT_REMEDIATION_REPORTS.some((blockedCustomer) =>
    normalizedCustomerName.startsWith(blockedCustomer),
  );
}

export interface LicenseInfo {
  licenseStatus: LicenseDTO | null | undefined;
  expiresAt: Date | null;
  numTargets: number | null;
  numTargetsUsed: number | null;
  numProbes: number | null;
  numProbesUsed: number | null;
  contractStartDate: Date | null;
  isExpired: boolean;
  cutOffDate: Date | null;
  isCutOff: boolean;
  isValid: boolean;

  // Soft warning states
  isTargetOverage: boolean;
  isProbeOverage: boolean;
  isApproachingTargetLimit: boolean;
  isApproachingProbeLimit: boolean;

  getStatusMessage: () => string;
  getExpirationMessage: () => string | null;
  getCutOffMessage: () => string | null;
  getTargetUsageMessage: () => string | null;
  getProbeUsageMessage: () => string | null;
  customerName: string | null;
}

export function createLicenseInfo(licenseStatus?: LicenseDTO | null): LicenseInfo {
  const expiresAt = licenseStatus?.expiresAt ? new Date(licenseStatus.expiresAt) : null;
  const contractStartDate = licenseStatus?.contractStartDate
    ? new Date(licenseStatus.contractStartDate)
    : null;
  const isExpired = expiresAt ? expiresAt < new Date() : false;
  const cutOffDate = expiresAt ? new Date(expiresAt.getTime() + 15 * 24 * 60 * 60 * 1000) : null;
  const isCutOff = cutOffDate ? cutOffDate < new Date() : false;
  // Soft warning states (allow scanning with warnings)
  const isTargetOverage = licenseStatus?.status === LicenseStatusEnum.TARGET_OVERAGE;
  const isProbeOverage = licenseStatus?.status === LicenseStatusEnum.PROBE_OVERAGE;
  const isApproachingTargetLimit =
    licenseStatus?.status === LicenseStatusEnum.APPROACHING_TARGET_LIMIT;
  const isApproachingProbeLimit =
    licenseStatus?.status === LicenseStatusEnum.APPROACHING_PROBE_LIMIT;
  const isValid = licenseStatus?.status === LicenseStatusEnum.OK;

  return {
    licenseStatus,
    expiresAt,
    numTargets: licenseStatus?.numLicensedTargets ?? null,
    numTargetsUsed: licenseStatus?.numTargetsUsed ?? null,
    numProbes: licenseStatus?.numLicensedProbes ?? null,
    numProbesUsed: licenseStatus?.numProbesUsed ?? null,
    contractStartDate,
    isExpired,
    cutOffDate,
    isCutOff,
    isValid,

    isTargetOverage,
    isProbeOverage,
    isApproachingTargetLimit,
    isApproachingProbeLimit,
    customerName: licenseStatus?.customerName ?? null,

    getStatusMessage(): string {
      if (!licenseStatus) {
        return 'No license found';
      }
      if (licenseStatus.status === LicenseStatusEnum.OK) {
        return `License is in good standing`;
      }
      if (licenseStatus.status === LicenseStatusEnum.NO_LICENSE) {
        return 'No license found';
      }
      if (
        licenseStatus.status === LicenseStatusEnum.TOO_MANY_TARGETS ||
        licenseStatus.status === LicenseStatusEnum.TARGET_OVERAGE
      ) {
        return 'Target limit exceeded';
      }
      if (
        licenseStatus.status === LicenseStatusEnum.TOO_MANY_PROBES ||
        licenseStatus.status === LicenseStatusEnum.PROBE_OVERAGE
      ) {
        return 'Probe limit exceeded';
      }
      if (licenseStatus.status === LicenseStatusEnum.APPROACHING_TARGET_LIMIT) {
        return 'Approaching target limit';
      }
      if (licenseStatus.status === LicenseStatusEnum.APPROACHING_PROBE_LIMIT) {
        return 'Approaching probe limit';
      }
      if (licenseStatus.status === LicenseStatusEnum.EXPIRED && expiresAt) {
        return `License expired on ${expiresAt.toLocaleDateString()}`;
      }
      return `License is invalid. Please contact support@promptfoo.dev.`;
    },

    getTargetUsageMessage(): string | null {
      if (!licenseStatus || !licenseStatus.numLicensedTargets) {
        return null;
      }

      const used = licenseStatus.numTargetsUsed ?? 0;
      const limit = licenseStatus.numLicensedTargets;

      if (isTargetOverage) {
        return `Using ${used} of ${limit} licensed targets (over limit - overage rates apply)`;
      }

      return `Using ${used} of ${limit} licensed targets`;
    },

    getExpirationMessage(): string | null {
      if (!expiresAt) {
        return null;
      }

      if (isExpired) {
        return `License expired on ${expiresAt.toLocaleDateString()}`;
      }

      return `Expires On: ${expiresAt.toLocaleDateString()}`;
    },

    getCutOffMessage(): string | null {
      if (!cutOffDate) {
        return null;
      }

      if (isCutOff) {
        return 'You still have access to your existing data but cannot start new scans.';
      }

      return `You will not be able to start new scans after ${cutOffDate.toLocaleDateString()}`;
    },

    getProbeUsageMessage(): string | null {
      if (!licenseStatus || !licenseStatus.numLicensedProbes) {
        return null;
      }

      const used = licenseStatus.numProbesUsed ?? 0;
      const limit = licenseStatus.numLicensedProbes;

      if (isProbeOverage) {
        return `Using ${used} of ${limit} licensed probes (over limit - overage rates apply)`;
      }

      return `Using ${used} of ${limit} licensed probes`;
    },
  };
}
